import { useMemo, useState, useEffect } from 'react';
import type { TunableParam } from '../../../types/TunableParam';
import { QuickTuneGroup } from './QuickTuneGroup';

interface QuickTunePanelProps {
    tunableParams: TunableParam[];
    onApplyChanges: (changes: any[]) => void;
    onSaveComparison: (name: string) => void;
    onCollapse?: () => void;

    // Lifted state props
    pendingChanges: Map<string, any>; // Map<string, PendingChange>
    onLocalChange: (nodeId: string, paramKey: string, newValue: number | boolean | string) => void;
    onReset: () => void;
    saveDisabled?: boolean;
}

export function QuickTunePanel({
    tunableParams,
    onApplyChanges,
    onSaveComparison,
    onCollapse,
    pendingChanges,
    onLocalChange,
    onReset,
    saveDisabled = false
}: QuickTunePanelProps) {

    // Persist collapse state in localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('quickTuneCollapsed');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const [showNamePopup, setShowNamePopup] = useState(false);
    const [comparisonName, setComparisonName] = useState("");

    const handleSaveConfirm = () => {
        if (comparisonName.trim()) {
            onSaveComparison(comparisonName);
            setShowNamePopup(false);
            setComparisonName(""); // reset
        }
    };

    useEffect(() => {
        localStorage.setItem('quickTuneCollapsed', JSON.stringify(isCollapsed));
    }, [isCollapsed]);

    // Memoize the grouping operation
    const groupedParams = useMemo(() => {
        if (tunableParams.length === 0) return {};

        return tunableParams.reduce((acc, param) => {
            const key = param.nodeId;
            if (!acc[key]) {
                acc[key] = {
                    nodeTitle: param.nodeTitle || `Node ${param.nodeId.substring(0, 8)}`,
                    nodePath: param.nodePath,
                    params: [],
                };
            }
            acc[key].params.push(param);
            return acc;
        }, {} as Record<string, { nodeTitle: string; nodePath: string; params: TunableParam[] }>);
    }, [tunableParams]);

    if (tunableParams.length === 0) {
        return null;
    }

    const hasChanges = pendingChanges.size > 0;

    const handleApply = () => {
        const changes: any[] = [];
        pendingChanges.forEach(change => {
            changes.push(change);
        });
        onApplyChanges(changes);
        // onReset(); // Keep changes in UI so they don't revert visually
    };

    return (
        <>
            {/* Toggle Button */}
            <button
                onClick={() => {
                    const next = !isCollapsed;
                    setIsCollapsed(next);
                    if (next && onCollapse) onCollapse();
                }}
                style={{
                    position: 'absolute',
                    right: isCollapsed ? 0 : 280,
                    top: 130,
                    width: 32,
                    height: 40,
                    background: 'var(--bg-panel)',
                    border: '1px solid var(--surface-glass-border)',
                    borderRight: isCollapsed ? '1px solid var(--surface-glass-border)' : 'none',
                    borderTopLeftRadius: 'var(--radius-md)',
                    borderBottomLeftRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    zIndex: 1001,
                    transition: 'right 0.3s ease',
                }}
                title={isCollapsed ? 'Expand Quick Tune' : 'Collapse Quick Tune'}
            >
                {isCollapsed ? '<<' : '>>'}
            </button>

            {/* Panel */}
            <div
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 130,
                    bottom: 0,
                    width: 280,
                    background: 'var(--bg-panel)',
                    borderLeft: '1px solid var(--surface-glass-border)',
                    boxShadow: 'var(--shadow-sm)',
                    overflowY: 'auto',
                    padding: '1rem',
                    zIndex: 1000,
                    transform: isCollapsed ? 'translateX(100%)' : 'translateX(0)',
                    transition: 'transform 0.3s ease',
                }}
            >
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>
                    ⚙️ Quick Tune
                </h3>

                {Object.entries(groupedParams).map(([nodeId, group]) => (
                    <QuickTuneGroup
                        key={nodeId}
                        nodeId={nodeId}
                        nodeTitle={group.nodeTitle}
                        nodePath={group.nodePath}
                        params={group.params}
                        pendingChanges={pendingChanges}
                        onLocalChange={onLocalChange}
                    />
                ))}

                {/* Action Buttons */}
                <div style={{
                    marginTop: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    borderTop: '1px solid var(--surface-glass-border)'
                }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={handleApply}
                            disabled={!hasChanges}
                            style={{
                                flex: 1,
                                background: 'var(--accent-primary)',
                                color: 'var(--text-primary)',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: !hasChanges ? 'not-allowed' : 'pointer',
                                opacity: !hasChanges ? 0.6 : 1,
                                fontSize: '0.85rem',
                                fontWeight: '600'
                            }}
                        >
                            Apply Changes
                        </button>

                        <button
                            onClick={onReset}
                            disabled={!hasChanges}
                            style={{
                                padding: '0.5rem',
                                background: 'transparent',
                                color: 'var(--text-muted)',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                            }}
                        >
                            Reset
                        </button>
                    </div>

                    <button
                        onClick={() => setShowNamePopup(true)} // Open popup
                        disabled={saveDisabled}
                        style={{
                            width: '100%',
                            background: saveDisabled ? 'var(--bg-panel)' : 'var(--bg-card)',
                            color: saveDisabled ? 'var(--text-muted)' : 'var(--text-primary)',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--surface-glass-border)',
                            cursor: saveDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                        }}
                    >
                        💾 Save Comparison
                    </button>
                </div>
            </div>

            {/* Naming Popup Modal */}
            {showNamePopup && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'var(--overlay-backdrop)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    backdropFilter: 'blur(2px)'
                }}>
                    <div style={{
                        background: 'var(--bg-panel)',
                        padding: '1.5rem',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--surface-glass-border)',
                        width: '300px',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>Name Comparison</h3>
                        <input
                            autoFocus
                            type="text"
                            placeholder="Enter name (e.g. Optimized Vol)"
                            value={comparisonName}
                            onChange={(e) => setComparisonName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveConfirm();
                                if (e.key === 'Escape') setShowNamePopup(false);
                            }}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                marginBottom: '1rem',
                                background: 'var(--bg-app)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--surface-glass-border)',
                                borderRadius: 'var(--radius-sm)'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <button
                                onClick={() => setShowNamePopup(false)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid var(--surface-glass-border)',
                                    color: 'var(--text-primary)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveConfirm}
                                disabled={!comparisonName.trim()}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: 'var(--accent-primary)',
                                    border: 'none',
                                    color: 'var(--text-primary)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: !comparisonName.trim() ? 'not-allowed' : 'pointer',
                                    opacity: !comparisonName.trim() ? 0.6 : 1
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

